import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Eraser, Wrench, Loader2, ShieldCheck } from "lucide-react";

const ORIGEN_LBL = { maestra: "Maestra", generada: "Generada", manual: "Manual" };

export const HerenciaRotaDialog = ({ open, maestras, onClose, onRepararHuerfana, refreshKey }) => {
  const [cargando, setCargando] = useState(false);
  const [lista, setLista] = useState([]);
  const [accionando, setAccionando] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/plantillas-salud/herencia-rota");
      setLista(data.plantillas || []);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { if (open) cargar(); }, [open, cargar, refreshKey]);

  const limpiarPadres = async (p) => {
    setAccionando(p.id);
    try {
      const { data } = await api.post(`/plantillas/${p.id}/limpiar-padres-huerfanos`);
      toast.success(`Referencia(s) huérfana(s) quitada(s): ${data.eliminados}`);
      await cargar(); onRepararHuerfana?.();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setAccionando(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="herencia-rota-dialog" className="bg-white max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle size={18} /> Plantillas con herencia rota
          </DialogTitle>
          <DialogDescription>
            Plantillas que heredan de un padre inexistente o cuya maestra fue borrada. Reparalas acá.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {cargando ? (
            <div className="py-10 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Analizando…
            </div>
          ) : lista.length === 0 ? (
            <div data-testid="herencia-rota-vacio" className="py-10 text-center text-teal-700 text-sm flex flex-col items-center gap-2">
              <ShieldCheck size={28} /> No hay plantillas con herencia rota. Todo en orden.
            </div>
          ) : (
            <ul className="space-y-2">
              {lista.map((p) => (
                <li key={p.id} data-testid={`herencia-rota-item-${p.id}`}
                  className="border border-amber-200 bg-amber-50/50 rounded-sm p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{p.nombre}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <span className="uppercase font-semibold">{ORIGEN_LBL[p.origen] || p.origen}</span>
                      {p.padres_inexistentes?.length > 0 && <> · hereda de padre inexistente</>}
                      {p.maestra_inexistente && <> · maestra borrada</>}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                    {p.padres_inexistentes?.length > 0 && (
                      <Button data-testid={`salud-limpiar-${p.id}`} size="sm" variant="outline"
                        disabled={accionando === p.id}
                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        onClick={() => limpiarPadres(p)}>
                        {accionando === p.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Eraser size={13} className="mr-1" />}
                        Quitar referencia
                      </Button>
                    )}
                    {p.origen === "generada" && (p.maestra_inexistente || p.padres_inexistentes?.length > 0) && (
                      <Button data-testid={`salud-reparar-${p.id}`} size="sm" variant="outline"
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => onRepararHuerfana?.({ id: p.id, nombre: p.nombre,
                          maestra_inexistente: p.maestra_inexistente })}>
                        <Wrench size={13} className="mr-1" /> Reasignar / desvincular
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} data-testid="herencia-rota-cerrar">Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
